import { DataEnum, DataType } from "models/groupBy";

/**
 * Formats a number to significant figures using standard rounding (round half up)
 * Handles floating-point precision issues with a small epsilon adjustment
 */
function formatToSigFigs(num: number, sigFigs: number): string {
  // Validate inputs
  if (!isFinite(num)) {
    return num.toString();
  }

  // Validate sigFigs is positive
  if (sigFigs < 1) {
    sigFigs = 1;
  }

  // Explicitly handle zero - format with appropriate precision
  // The leading 0 counts as 1 sig fig, so: 0 with 5 sig figs = "0.0000" (4 decimal places)
  if (num === 0) {
    if (sigFigs === 1) {
      return "0";
    }
    return "0." + "0".repeat(sigFigs - 1);
  }

  // Cap sigFigs at reasonable maximum to prevent overflow
  if (sigFigs > 10) {
    sigFigs = 10;
  }

  // Add a small epsilon to handle floating-point precision issues
  // This ensures values like 1234.55 (which is stored as 1234.5499999...)
  // round correctly to 1234.6 instead of 1234.5
  const sign = Math.sign(num);
  const absNum = Math.abs(num);
  const magnitude = Math.floor(Math.log10(absNum));

  // Guard against potential overflow in Math.pow
  const exponent = magnitude - sigFigs;
  if (exponent > 308 || exponent < -308) {
    // Outside safe range for Math.pow, return as-is with toPrecision
    const formatted = num.toPrecision(sigFigs);
    const parsed = parseFloat(formatted);
    return parsed.toString();
  }

  const epsilon = Math.pow(10, exponent) * 1e-10;
  const adjusted = absNum + epsilon;

  // Use toPrecision which rounds half up
  const formatted = (sign * adjusted).toPrecision(sigFigs);

  // If in scientific notation, convert to regular notation (may lose some trailing zeros)
  if (formatted.includes("e") || formatted.includes("E")) {
    const parsed = parseFloat(formatted);
    return parsed.toString();
  }

  // Remove trailing decimal point if it's just a decimal point (e.g., "5." -> "5")
  if (formatted.endsWith(".")) {
    return formatted.slice(0, -1);
  }

  // Preserve trailing zeros for regular notation
  return formatted;
}

/**
 * Helper function to preserve trailing zeros that represent significant figures
 * Only removes the trailing decimal point if there are no decimal digits
 */
function preserveSignificantZeros(formatted: string): string {
  // Only remove trailing decimal point if it's just a decimal point (e.g., "5." -> "5")
  if (formatted.endsWith(".")) {
    return formatted.slice(0, -1);
  }

  // Preserve all trailing zeros as they represent significant figures
  return formatted;
}

/**
 * Formats a number as a string with the specified number of significant figures.
 * This function preserves trailing zeros when they represent significant figures.
 *
 * @param num - The number to format
 * @param sigFigs - Number of significant figures
 * @returns A formatted string representation of the number
 */
export function niceNumString(num: number, sigFigs: number): string {
  const formatted = formatToSigFigs(num, sigFigs);
  return preserveSignificantZeros(formatted);
}

/**
 * Formats a number as a string with significant figures, but ensures that for integers,
 * it doesn't round down the significant figures below what's needed to represent the integer part.
 * Preserves trailing zeros when they represent significant figures.
 *
 * @param num - The number to format
 * @param sigFigs - Minimum number of significant figures
 * @returns A formatted string representation of the number
 */
export function niceNumStringNoIntRounding(num: number, sigFigs: number): string {
  // For numbers >= 1, ensure we have enough sig figs to represent the integer part
  if (isFinite(num) && num !== 0 && Math.abs(num) >= 1.0) {
    const minSigFigs = Math.max(sigFigs, Math.floor(Math.log10(Math.abs(num))) + 1);
    const formatted = formatToSigFigs(num, minSigFigs);
    return preserveSignificantZeros(formatted);
  }

  // For numbers < 1, use standard significant figures
  return niceNumString(num, sigFigs);
}
/**
 * Formats a number using the appropriate precision function based on rounding mode.
 *
 * @param value - The number to format
 * @param defaultSignificantFigures - Number of significant figures to use (must be >= 1)
 * @param useStrictRounding - Whether to use strict rounding (niceNumString) or preserve integer digits (niceNumStringNoIntRounding)
 * @returns Formatted number string
 */
export function formatNumber(
  value: number,
  defaultSignificantFigures: number,
  useStrictRounding: boolean,
  dataType?: DataType
): string {
  // If dataType is "Integer", return the rounded value without decimal places
  if (dataType === DataEnum.Integer) {
    return Math.round(value).toString();
  }

  // Validate defaultSignificantFigures
  let sigFigs = defaultSignificantFigures;

  if (!isFinite(sigFigs) || sigFigs < 1) {
    sigFigs = 1;
  }

  // Cap at reasonable maximum
  if (sigFigs > 10) {
    sigFigs = 10;
  }

  if (useStrictRounding) {
    return niceNumString(value, sigFigs);
  } else {
    return niceNumStringNoIntRounding(value, sigFigs);
  }
}
